import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { AddProposalNameSearchComponent } from '../add-proposal-name-search/add-proposal-name-search.component';
import { CompanyVoteProposalNameFormComponent } from '../../../../../voting-results/company-vote-proposal-name-form/company-vote-proposal-name-form.component';
import { CompanyVoteProposalNameService } from '../../../../../../services/company-vote-proposal-name.service';
import { CreateCompanyVoteProposalNameRequest } from '../../../../../../models/api/createCompanyVoteProposalNameRequest';
import { GlobalSearchResultDto } from '../../../../../../models/api/globalSearchResultDto';
import { EntityType } from '../../../../../../models/enums/entityType.enum';
import { Helpers } from '../../../../../../utilities/helpers';

export interface AddProposalNameDialogData {
  year: string;
}

export interface AddProposalNameDialogResult {
  action: 'connect' | 'created';
  item: GlobalSearchResultDto;
}

type DialogMode = 'add-new' | 'search-connect';

@Component({
  selector: 'app-add-proposal-name-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    NgxSpinnerModule,
    AddProposalNameSearchComponent,
    CompanyVoteProposalNameFormComponent
  ],
  templateUrl: './add-proposal-name-dialog.component.html',
  styleUrls: ['./add-proposal-name-dialog.component.scss']
})
export class AddProposalNameDialogComponent {
  private fb = inject(FormBuilder);
  private proposalNameService = inject(CompanyVoteProposalNameService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private data = inject<AddProposalNameDialogData>(MAT_DIALOG_DATA, { optional: true });

  currentMode: DialogMode = 'search-connect';
  selectedItem: GlobalSearchResultDto | null = null;

  createForm: FormGroup = this.fb.group({
    year: [this.data?.year || Helpers.defaultMeasurementYear(), Validators.required],
    fullName: ['', Validators.required],
    address: [''],
    website: [''],
    email: [''],
    country: [''],
    category: [''],
    subcategory: [''],
    notes: ['']
  });

  constructor(
    public dialogRef: MatDialogRef<AddProposalNameDialogComponent>
  ) {}

  getDialogTitle(): string {
    switch (this.currentMode) {
      case 'add-new':
        return 'Create New Proposal Name';
      case 'search-connect':
        return 'Add Proposal Name';
      default:
        return 'Add Proposal Name';
    }
  }

  onAddNewFromSearch(): void {
    this.currentMode = 'add-new';
  }

  onBack(): void {
    this.currentMode = 'search-connect';
    this.selectedItem = null;
  }

  onProposalNameSelected(item: GlobalSearchResultDto | null): void {
    this.selectedItem = item;
  }

  onCreateSubmit(): void {
    if (!this.createForm.valid) return;

    const formValue = this.createForm.value;
    const request: CreateCompanyVoteProposalNameRequest = {
      year: parseInt(formValue.year),
      fullName: formValue.fullName,
      address: formValue.address || '',
      website: formValue.website || '',
      email: formValue.email || '',
      countryId: formValue.country?.id || formValue.country || '',
      categoryId: formValue.category?.id || formValue.category || '',
      subCategoryId: formValue.subcategory?.id || formValue.subcategory || '',
      notes: formValue.notes || ''
    };

    this.spinnerService.show();
    this.proposalNameService.create(request).subscribe({
      next: (created) => {
        this.spinnerService.hide();
        this.toast.success('Proposal name created successfully');
        const result: AddProposalNameDialogResult = {
          action: 'created',
          item: {
            id: created.id,
            name: created.fullName,
            entityType: EntityType.VoteProposalName
          }
        };
        this.dialogRef.close(result);
      },
      error: (error) => {
        this.spinnerService.hide();
        this.toast.error('Error creating proposal name');
        console.error('Error creating proposal name:', error);
      }
    });
  }

  onConfirmConnect(): void {
    if (!this.selectedItem) return;
    const result: AddProposalNameDialogResult = {
      action: 'connect',
      item: this.selectedItem
    };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}
